// @flow

import * as React from 'react';

import {
  selectCartIsCommitted,
  selectIsFetchingCartConfigurations,
  selectIsLoadedCartConfigurations,
  fetchCartConfigurations,
  selectCartUUID,
} from '@gandi/cart';
import { selectors as templateSelectors } from '@gandi/template2';
import { useTranslator } from '@gandi/translate';
import { Alert, Base, Spinner } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { PreFetchArgs, PreFetchReturn } from '../../../../../types';
import { selectSetupOwner } from '../../../modules/selectors';
import type { CreateOrTransfer } from '../../../types';

import Message from './components/Message';
import { selectConfiguration } from './modules/selectors';
import type { TemplateKind } from './types';

type Props = {
  process: CreateOrTransfer,
};

const getTemplateKind: (
  id: string,
  defaultTemplateId: string,
) => TemplateKind = (id, defaultTemplateId) => {
  if (!id) {
    return 'default';
  }
  if (defaultTemplateId === id) {
    return 'predefined';
  }
  return 'custom';
};

function Template({ process }: Props): React.Node {
  const translator = useTranslator();

  const cartId = useAppSelector(selectCartUUID);

  const committed = useAppSelector(selectCartIsCommitted);
  const configuration = useAppSelector(state =>
    selectConfiguration(state, process),
  );

  const setupOwner = useAppSelector(selectSetupOwner);

  const shouldFetchCartConfiguration = useAppSelector(
    state =>
      !selectIsFetchingCartConfigurations(state) &&
      !selectIsLoadedCartConfigurations(state),
  );

  const isFetching = useAppSelector(state => {
    const defaultTemplateArgs = {
      productType: 'domain',
      process,
      sharingId: setupOwner,
    };

    return (
      !templateSelectors.selectIsLoadedOneDefaultTemplate(
        state,
        defaultTemplateArgs,
      ) ||
      templateSelectors.selectIsFetchingOneDefaultTemplate(
        state,
        defaultTemplateArgs,
      ) ||
      selectIsFetchingCartConfigurations(state) ||
      !selectIsLoadedCartConfigurations(state)
    );
  });

  React.useEffect(() => {
    if (shouldFetchCartConfiguration) {
      fetchCartConfigurations();
    }
  }, [shouldFetchCartConfiguration]);

  const defaultTemplateId = useAppSelector(state =>
    templateSelectors.defaultTemplateSelector(state, {
      productType: 'domain',
      process,
      sharingId: setupOwner,
    }),
  );

  const templateChangeURL = `/cart/${cartId}/configure/templates?productType=domain&process=${process}`;

  const payload = configuration?.payload || {};
  const id = payload.template_id || '';

  // $FlowFixMe[incompatible-call]
  const templateKind = getTemplateKind(id, defaultTemplateId);

  const locked = payload.locked || false;

  const removeLink =
    !locked &&
    !committed &&
    process === 'transfer' &&
    ['custom', 'predefined'].includes(templateKind);

  const message = (
    <Message
      templateName={payload.template_name}
      process={process}
      changeURL={!locked && !committed && templateChangeURL}
      removeLink={removeLink}
      templateKind={templateKind}
      configuration={configuration}
    />
  );

  const alert = process === 'transfer' && templateKind === 'default';

  return (
    <>
      <Base tagName="h1" marginBottom="small">
        {translator.__('Template Configuration')}
      </Base>

      {isFetching && <Spinner centered />}

      {!isFetching && alert && (
        <Alert message={message} intent="warning" marginBottom="small" />
      )}

      {!isFetching && !alert && (
        <Base padding="small" backgroundColor="surface" marginBottom="base">
          {message}
        </Base>
      )}
    </>
  );
}

Template.preFetch = (props: PreFetchArgs<{}>): PreFetchReturn<{}> =>
  props.dispatch(fetchCartConfigurations());

export default Template;
